import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import type { WorkflowJob, JobType } from '../graphql/types';
import * as workflowService from '../services/workflowService';
import { graphqlClient } from '../services/graphqlClient';

/**
 * useWorkflowJob: loads a DD/BA job for the Workspace and keeps it fresh.
 *
 * Moved out of `WorkflowJobPage` unchanged in behaviour:
 * - initial load through `workflowService.getJob` (tenant from the ID token),
 * - `onJobStatusChanged` subscription with up to 3 reconnect attempts,
 * - polling fallback every 5 seconds after repeated failures, on auth errors,
 *   and in preview mode.
 *
 * The per-document subscription (`onDocumentStatusChanged`) is not used by the
 * legacy DD/BA page: the step components own it, so it stays with them until
 * the Mode bodies replace those steps.
 *
 * Requirements: 3.4 (Workspace owns the data so Mode changes keep the selection),
 * 3.6 (title and Status_Pill come from the loaded job), 12.1, 12.3, 12.5.
 */

export const MAX_RECONNECT_ATTEMPTS = 3;
export const POLLING_INTERVAL_MS = 5000;

// Preview mode has no AWS backend: use polling instead of subscriptions.
const isPreviewMode =
  import.meta.env.VITE_PREVIEW === 'true' || !import.meta.env.VITE_COGNITO_USER_POOL_ID;

export interface UseWorkflowJobResult {
  job: WorkflowJob | null;
  /** True while a load is in flight (initial load, retry or reload). */
  loading: boolean;
  error: string | null;
  /** Re-fetch the job. Keeps showing the current job while it loads. */
  reload: () => Promise<void>;
  /** Replace the job with a fresher copy from a child component. */
  updateJob: (job: WorkflowJob) => void;
}

function isAuthError(err: any): boolean {
  const message: string = err?.message || err?.toString() || '';
  return (
    message.includes('NotAuthorizedException') ||
    message.includes('Invalid login token') ||
    message.includes('Unauthorized')
  );
}

/** Behaviour switches for Features whose legacy page differed from DD/BA. */
export interface UseWorkflowJobOptions {
  /**
   * Merge pushed updates (subscription, polling, `updateJob`) into the current
   * job instead of replacing it, so a partial payload never drops fields.
   * Title Search (TR) uses this. Default: false (replace).
   */
  mergeUpdates?: boolean;
  /**
   * Re-fetch the job whenever the subscription is re-established after an
   * error, to pick up updates missed in between. Title Search (TR) uses this.
   * Default: false.
   */
  reconcileOnReconnect?: boolean;
}

export function useWorkflowJob(
  jobId: string,
  jobType: JobType,
  options: UseWorkflowJobOptions = {}
): UseWorkflowJobResult {
  const [job, setJob] = useState<WorkflowJob | null>(null);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  /** Apply a pushed update according to `mergeUpdates`. */
  const applyUpdate = useCallback((updated: WorkflowJob) => {
    if (optionsRef.current.mergeUpdates) {
      // A partial payload (for example the one `updateStepStatus` publishes)
      // arrives with the fields it does not carry set to null: keep what we
      // have for those instead of blanking them. A full re-fetch (`reload`)
      // still replaces the job.
      const present = Object.fromEntries(
        Object.entries(updated).filter(([, value]) => value !== null && value !== undefined)
      );
      setJob((prev) => ({ ...(prev ?? {}), ...present }) as WorkflowJob);
    } else {
      setJob(updated);
    }
  }, []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reconnectAttemptsRef = useRef(0);
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const subscriptionRef = useRef<any>(null);
  const isPollingRef = useRef(false);
  const disposedRef = useRef(false);
  // Identifies the job the hook currently points at, so a response for a
  // previous route param is dropped.
  const currentKeyRef = useRef('');
  currentKeyRef.current = `${jobType}:${jobId}`;

  /** Fetch job data. Requirement 9.6: supports resuming from the last step. */
  const reload = useCallback(async () => {
    const key = `${jobType}:${jobId}`;
    setLoading(true);
    setError(null);

    try {
      const session = await fetchAuthSession();
      const tenantId =
        (session.tokens?.idToken?.payload['custom:tenantId'] as string) || 'default-tenant';

      const jobData = await workflowService.getJob(jobId, tenantId);
      if (disposedRef.current || currentKeyRef.current !== key) return;

      if (!jobData) {
        setError('Job not found. It may have been deleted.');
        setLoading(false);
        return;
      }

      if (jobData.jobType !== jobType) {
        setError(`Job type mismatch. Expected ${jobType} but found ${jobData.jobType}.`);
        setLoading(false);
        return;
      }

      setJob(jobData);
      setLoading(false);
    } catch (err) {
      if (disposedRef.current || currentKeyRef.current !== key) return;
      console.error('Failed to load job data:', err);
      setError('Failed to load job data. Please try again.');
      setLoading(false);
    }
  }, [jobId, jobType]);

  // Load on mount and when the route points at another job.
  useEffect(() => {
    setJob(null);
    reload();
  }, [reload]);

  const handleSubscriptionUpdate = useCallback(
    (updatedJob: WorkflowJob) => {
      if (updatedJob.jobId !== jobId) return;
      applyUpdate(updatedJob);
    },
    [jobId, applyUpdate]
  );

  /** Requirement 12.5: poll every 5 seconds when the subscription is unavailable. */
  const startPollingFallback = useCallback(() => {
    if (isPollingRef.current) return;
    isPollingRef.current = true;

    pollingIntervalRef.current = setInterval(async () => {
      try {
        const session = await fetchAuthSession();
        const tenantId =
          (session.tokens?.idToken?.payload['custom:tenantId'] as string) || 'default-tenant';
        const jobData = await workflowService.getJob(jobId, tenantId);
        if (!disposedRef.current && jobData && jobData.jobId === jobId) {
          applyUpdate(jobData);
        }
      } catch (err) {
        console.error('[useWorkflowJob] Polling failed:', err);
      }
    }, POLLING_INTERVAL_MS);
  }, [jobId, applyUpdate]);

  const stopPollingFallback = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    isPollingRef.current = false;
  }, []);

  /** Requirements 12.1, 12.3, 12.5: subscribe to onJobStatusChanged with reconnection. */
  const setupSubscription = useCallback(async () => {
    if (disposedRef.current) return;
    if (isPreviewMode) {
      startPollingFallback();
      return;
    }

    const retryOrPoll = () => {
      reconnectAttemptsRef.current += 1;
      if (reconnectAttemptsRef.current <= MAX_RECONNECT_ATTEMPTS) {
        setTimeout(() => {
          if (disposedRef.current) return;
          // Reconcile any updates missed while the subscription was down.
          if (optionsRef.current.reconcileOnReconnect) void reload();
          setupSubscription();
        }, 1000 * reconnectAttemptsRef.current);
      } else {
        startPollingFallback();
      }
    };

    try {
      const session = await fetchAuthSession();

      if (!session.tokens?.idToken) {
        console.warn('[useWorkflowJob] No valid auth token available for subscription');
        startPollingFallback();
        return;
      }

      const tenantId =
        (session.tokens.idToken.payload['custom:tenantId'] as string) || 'default-tenant';
      const username =
        (session.tokens.idToken.payload['cognito:username'] as string) || 'unknown';

      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }

      stopPollingFallback();

      const sub = graphqlClient.subscriptions.onJobStatusChanged(tenantId, username, jobType);

      subscriptionRef.current = (sub as any).subscribe({
        next: ({ data }: any) => {
          if (data?.onJobStatusChanged) {
            reconnectAttemptsRef.current = 0;
            handleSubscriptionUpdate(data.onJobStatusChanged);
          }
        },
        error: async (err: any) => {
          console.error('[useWorkflowJob] Subscription error:', err);
          if (isAuthError(err)) {
            startPollingFallback();
            return;
          }
          retryOrPoll();
        },
      });
    } catch (err: any) {
      console.error('[useWorkflowJob] Failed to setup subscription:', err);
      if (isAuthError(err)) {
        startPollingFallback();
        return;
      }
      retryOrPoll();
    }
  }, [jobType, reload, handleSubscriptionUpdate, startPollingFallback, stopPollingFallback]);

  useEffect(() => {
    disposedRef.current = false;
    setupSubscription();

    return () => {
      disposedRef.current = true;
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
      stopPollingFallback();
      reconnectAttemptsRef.current = 0;
    };
  }, [setupSubscription, stopPollingFallback]);

  const updateJob = useCallback((updated: WorkflowJob) => applyUpdate(updated), [applyUpdate]);

  return { job, loading, error, reload, updateJob };
}

export default useWorkflowJob;
